import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  routesForAuthenticatedOnly,
  routesForNotAuthenticatedOnly,
} from "../../../app/routes";
import useRoutePathMatch from "@jumbo/hooks/useRoutePathMatch";
import { removeToken, storeToken } from "./authHelpers";
import { config } from "../../../app/config/main";
import { AuthContext } from "@jumbo/components/JumboAuthProvider/JumboAuthContext";
import { getToken } from "app/services/authService";
import { hasPermission } from "app/hooks/usePermissions";
import { PermissionKeys } from "app/types/permissions";

const storedToken = getToken();

const init = () => {
  let authUser = null;

  if (!config?.authSetting) {
    throw Error(
      `You are using JumboAuthProvider but you haven't setup authSetting inside /src/app/config/main.js's config object`,
    );
  }

  if (storedToken) {
    storeToken(storedToken); // also sets axios header
  }

  const authUserString = localStorage.getItem("authUser");
  const activeAuthUser = authUserString ? JSON.parse(authUserString) : null;

  return {
    authToken: storedToken ?? null,
    authUser: activeAuthUser || authUser,
    isLoading: false,
    fallbackPath: config.authSetting.fallbackPath,
  };
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "set-auth-data":
      return {
        ...state,
        ...action.payload,
      };

    case "start-loading":
      return {
        ...state,
        isLoading: true,
      };

    case "stop-loading":
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
};

const JumboAuthProvider = ({ children, ...restProps }) => {
  const [authOptions, setAuthOptions] = React.useReducer(
    authReducer,
    restProps,
    init,
  );
  const [logout, setLogout] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticatedRouteOnly = useRoutePathMatch([
    ...routesForAuthenticatedOnly,
  ]);
  const isNotAuthenticatedRouteOnly = useRoutePathMatch([
    ...routesForNotAuthenticatedOnly,
  ]);

  React.useEffect(() => {
    if (logout) {
      removeToken();
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: null, authUser: null, isLoading: false },
      });
      setLogout(false);
    }
  }, [logout]);

  const setAuthToken = React.useCallback(async (token) => {
    setAuthOptions({ type: "start-loading" });
    if (!token) {
      setLogout(true);
      return;
    }

    storeToken(token);
    try {
      const authUser = await config?.authSetting?.getAuthUserService();
      if (authUser) {
        setAuthOptions({
          type: "set-auth-data",
          payload: { authToken: token, authUser: authUser, isLoading: false },
        });

        // set the authuser data on localstorage.
        localStorage.setItem("authUser", JSON.stringify(authUser));
        return;
      }
      setLogout(true);
    } catch (error) {
      setLogout(true);
      console.error(error);
    }
  }, []);

  //todo: maybe in next version
  const setRedirectPath = React.useCallback((redirectPath) => {
    setAuthOptions({ type: "set-redirect-path", payload: { redirectPath } });
  }, []);

  const setAuthData = React.useCallback((data) => {
    setAuthOptions({ type: "set-auth-data", payload: data });
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      ...authOptions,
      setAuthData,
      setRedirectPath,
      setAuthToken,
      setAuthOptions,
    };
  }, [authOptions]);

  React.useEffect(() => {
    if (!authOptions.authToken && isNotAuthenticatedRouteOnly) {
      return;
    } else if (authOptions.authToken && isNotAuthenticatedRouteOnly) {
      if (hasPermission(PermissionKeys.SUBSCRIPTION_VIEW)) {
        navigate("/dashboards/subscriptions");
      } else if (hasPermission(PermissionKeys.DELIVERY_VIEW)) {
        navigate("/dashboards/delivery-orders");
      } else if (hasPermission(PermissionKeys.PRODUCTION_VIEW)) {
        navigate("/dashboards/productions");
      } else if (hasPermission(PermissionKeys.MEALS_VIEW)) {
        navigate("/dashboards/menus/catalogs");
      } else if (hasPermission(PermissionKeys.CUSTOM_MEALS_VIEW)) {
        navigate("/dashboards/custom-meals");
      } else if (hasPermission(PermissionKeys.ORDERS_VIEW)) {
        navigate("/dashboards/orders");
      }
    } else if (isAuthenticatedRouteOnly && !authOptions.authToken) {
      navigate(authOptions?.fallbackPath);
    } else if (isAuthenticatedRouteOnly && authOptions.authToken) {
      return;
    } else if (authOptions.authToken) {
      navigate(config.authSetting.redirectNotAuthenticatedPath);
    } else {
      navigate(authOptions?.fallbackPath);
    }
  }, [authOptions.authUser, location]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default JumboAuthProvider;
